import type { Cart, CartCountItem } from '../types/Cart.ts';

export type CartSyncStatus = 'idle' | 'debouncing' | 'syncing' | 'failed';

interface CartSyncQueueOptions {
  confirm: () => Promise<Cart>;
  debounceMs?: number;
  initialConfirmedItems?: CartCountItem[];
  onConfirmed: (cart: Cart) => void;
  onDesiredChange: (items: CartCountItem[]) => void;
  onStatusChange: (status: CartSyncStatus, error?: unknown) => void;
  onSynchronized?: (result: CartSynchronizationResult) => void;
  retryDelaysMs?: number[];
  send: (items: CartCountItem[]) => Promise<void>;
}

export interface CartSynchronizationResult {
  confirmedItems: CartCountItem[];
  previousConfirmedItems: CartCountItem[];
  submittedItems: CartCountItem[];
}

function cloneItems(items: CartCountItem[]) {
  return items.map((item) => ({ ...item }));
}

function delay(timeout: number) {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

export function getCartCountItems(cart: Cart): CartCountItem[] {
  return cart.items.map(({ id, count }) => ({ id, count }));
}

export function getChangedCartItemIds(confirmedItems: CartCountItem[], desiredItems: CartCountItem[]): number[] {
  const confirmedCounts = new Map(confirmedItems.map((item) => [item.id, item.count]));
  const desiredCounts = new Map(desiredItems.map((item) => [item.id, item.count]));

  return [...new Set([...confirmedCounts.keys(), ...desiredCounts.keys()])].filter(
    (offerId) => confirmedCounts.get(offerId) !== desiredCounts.get(offerId),
  );
}

export function normalizeCartResponse(value: unknown): Cart | null {
  if (!value || typeof value !== 'object') return null;

  const cart = value as Partial<Cart>;

  if (
    !Array.isArray(cart.items) ||
    !cart.items.every((item) => typeof item?.id === 'number' && typeof item?.count === 'number') ||
    typeof cart.status !== 'string'
  ) {
    return null;
  }

  const hasCalculatedCart = Boolean(
    cart.calculate && !Array.isArray(cart.calculate) && typeof cart.calculate === 'object',
  );

  if (cart.items.length > 0 && (!hasCalculatedCart || typeof cart.cart_id !== 'number')) return null;

  return {
    calculate: hasCalculatedCart
      ? cart.calculate!
      : {
          discount_data: [],
          discounts: [],
          loyalty: {
            action: 'income',
            available_to_withdraw: 0,
            income: 0,
          },
          offer_price_and_discount: [],
          price_without_discounts: 0,
          promo_code: undefined,
          total: 0,
          totalDiscounts: {},
        },
    cart_id: typeof cart.cart_id === 'number' ? cart.cart_id : 0,
    items: cart.items,
    promo_errors: cart.promo_errors,
    promo_warnings: cart.promo_warnings,
    quantity_error: cart.quantity_error,
    restrictions: cart.restrictions ?? { required_gism: false },
    status: cart.status,
  };
}

export function isCartResponse(value: unknown): value is Cart {
  return normalizeCartResponse(value) !== null;
}

export class CartSyncQueue {
  private readonly confirm: CartSyncQueueOptions['confirm'];
  private readonly debounceMs: number;
  private readonly onConfirmed: CartSyncQueueOptions['onConfirmed'];
  private readonly onDesiredChange: CartSyncQueueOptions['onDesiredChange'];
  private readonly onStatusChange: CartSyncQueueOptions['onStatusChange'];
  private readonly onSynchronized: CartSyncQueueOptions['onSynchronized'];
  private readonly retryDelaysMs: number[];
  private readonly send: CartSyncQueueOptions['send'];

  private confirmedItems: CartCountItem[];
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;
  private desiredItems: CartCountItem[] = [];
  private drainPromise: Promise<boolean> | undefined;
  private revision = 0;
  private synchronizedRevision = 0;
  private status: CartSyncStatus = 'idle';

  constructor(options: CartSyncQueueOptions) {
    this.confirm = options.confirm;
    this.confirmedItems = cloneItems(options.initialConfirmedItems ?? []);
    this.debounceMs = options.debounceMs ?? 250;
    this.onConfirmed = options.onConfirmed;
    this.onDesiredChange = options.onDesiredChange;
    this.onStatusChange = options.onStatusChange;
    this.onSynchronized = options.onSynchronized;
    this.retryDelaysMs = options.retryDelaysMs ?? [1000, 3000];
    this.send = options.send;
  }

  enqueue(items: CartCountItem[]) {
    this.desiredItems = cloneItems(items);
    this.revision += 1;
    this.onDesiredChange(cloneItems(this.desiredItems));

    if (this.drainPromise) return;

    this.clearDebounce();
    this.setStatus('debouncing');
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = undefined;
      void this.startDrain();
    }, this.debounceMs);
  }

  acceptConfirmed(cart: Cart) {
    if (this.hasPendingChanges()) return false;

    this.confirmedItems = getCartCountItems(cart);
    this.desiredItems = cloneItems(this.confirmedItems);
    this.onDesiredChange(cloneItems(this.desiredItems));
    this.onConfirmed(cart);

    return true;
  }

  async flush() {
    this.clearDebounce();

    if (!this.hasPendingChanges()) return true;

    return await this.startDrain();
  }

  async retry() {
    if (this.status !== 'failed') return await this.flush();

    this.setStatus('debouncing');

    return await this.startDrain();
  }

  private clearDebounce() {
    if (!this.debounceTimer) return;

    clearTimeout(this.debounceTimer);
    this.debounceTimer = undefined;
  }

  private hasPendingChanges() {
    return this.revision > this.synchronizedRevision || this.status !== 'idle';
  }

  private async sendWithRetry(items: CartCountItem[], sendingRevision: number) {
    let lastError: unknown;

    for (let attempt = 0; attempt <= this.retryDelaysMs.length; attempt += 1) {
      if (attempt > 0) await delay(this.retryDelaysMs[attempt - 1]);
      if (sendingRevision !== this.revision) return false;

      try {
        await this.send(cloneItems(items));
        return true;
      } catch (error) {
        lastError = error;

        if (sendingRevision !== this.revision) return false;
      }
    }

    throw lastError;
  }

  private setStatus(status: CartSyncStatus, error?: unknown) {
    this.status = status;
    this.onStatusChange(status, error);
  }

  private startDrain() {
    if (this.drainPromise) return this.drainPromise;

    this.setStatus('syncing');
    this.drainPromise = this.drain().finally(() => {
      this.drainPromise = undefined;
    });

    return this.drainPromise;
  }

  private async drain() {
    try {
      while (this.synchronizedRevision < this.revision) {
        const sendingRevision = this.revision;
        const itemsToSend = cloneItems(this.desiredItems);

        const wasSent = await this.sendWithRetry(itemsToSend, sendingRevision);

        if (!wasSent) continue;

        if (sendingRevision !== this.revision) continue;

        const confirmedCart = await this.confirm();

        if (sendingRevision !== this.revision) continue;

        const previousConfirmedItems = cloneItems(this.confirmedItems);
        const confirmedItems = getCartCountItems(confirmedCart);

        this.confirmedItems = cloneItems(confirmedItems);
        this.desiredItems = cloneItems(confirmedItems);
        this.synchronizedRevision = sendingRevision;
        this.onDesiredChange(cloneItems(this.desiredItems));
        this.onConfirmed(confirmedCart);
        this.onSynchronized?.({
          confirmedItems: cloneItems(confirmedItems),
          previousConfirmedItems,
          submittedItems: cloneItems(itemsToSend),
        });
      }

      this.setStatus('idle');

      return true;
    } catch (error) {
      this.setStatus('failed', error);

      return false;
    }
  }
}
